import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Lead } from '../ui/Panel';
import Button from '../ui/Button';
import MoreMenu from '../ui/MoreMenu';
import { promptAction } from '../ui/Confirm';
import { SkeletonRows, ErrorNotice, InfoNotice, EmptyState } from '../ui/States';
import { StatusMark } from '../ui/Pill';
import { fmt } from '../../lib/format';
import { errorMessage, selectClass } from '../../lib/programs';
import type { FollowupItem, Followups, PanelProps } from './types';
import { headRow, th, td, num, em, monthDay } from './logistics';

type Filter = 'open' | 'done' | 'all';

/** Each kind's rule, said once above its rows. */
const WHY: Record<string, string> = {
  fellow_no_registration:
    'Attendance is a Fellow commitment, and joining online counts. Remind them, or mark done if they’ve told you directly.',
  speaker_not_confirmed: 'Confirm whether they’re coming and in what format, then set their status on People.',
  speaker_room: 'Your answer sets the hotel count. Rooms are only counted once you say a speaker needs one.',
  speaker_details: 'Dinner, arrival time and consent to record. Ask for the missing piece.',
  speaker_slides: 'Slides are due before the event, for the AV team and the interpreters. Ask for them.',
  speaker_glossary: 'The interpreters need the speaker’s glossary ahead of time. Ask for it.',
  speaker_prep: 'Set up the prep meeting between the speaker and their moderator.',
  speaker_no_form: 'These speakers are not on the list and haven’t given their details (dietary, schedule). Ask them.',
  parking_code_not_sent: 'Send each person their parking code for the days they asked for.',
  eventbrite_core: 'Core members don’t need a ticket. Check whether a refund is needed.',
  eventbrite_on_list:
    'They bought a ticket but are already on the list under another email, so the same person is counted twice.',
  eventbrite_other_email:
    'An Eventbrite ticket under another email had exactly their name, so it was put on them instead of a second row. Mark done if it’s the same person.',
  flying_no_hotel: 'They’re flying in but didn’t ask for a hotel. Confirm they don’t need a room.',
  local_elsewhere: 'They said they’re within an hour of the venue, but look like they’re from elsewhere.',
  dietary_unclear: 'The dietary answer names a food without saying what to do with it. Confirm what it means.',
  accommodations_no_details: 'They answered “yes” to other accommodations with no details. Ask what they need.',
  accommodation_request: 'Arrange what they asked for with the venue, or reply to them.',
  extra_nights:
    'They asked for more nights than the travel rules cover, or to arrive early. Reply and set the hotel nights.',
  hotel_outside_block: 'Some hotel nights fall outside the room block. Confirm the rate and billing with the hotel.',
  hotel_outside_event: 'The hotel dates don’t cover the days they attend. Confirm the hotel dates.',
  hotel_before_session: 'They check out before their own session. Confirm the hotel dates.',
  hotel_list_no_response: 'On the hotel’s list but no form response. Confirm travel, dietary needs and banking status.',
  hotel_list_unmatched: 'On the hotel’s list but not on the event list. Add them, or check the name.',
  hotel_name_mismatch: 'The name on the hotel list differs. Make sure it matches their ID at check-in.',
  banking_request: 'Send the direct-deposit request. York finance collects the details; never by email.',
  eligibility: 'Their answers fall outside the event’s travel rules. Decide, or log a travel exception.',
  travel_exception_pending: 'Waiting on the NPI’s decision. Nothing to do yet.',
  pheedloop_not_sent: 'Registered people need PheedLoop access before the event. Add them on Documents (PheedLoop).',
};

/** What to do, for the "Smaller kinds" table. */
const TODO: Record<string, string> = {
  fellow_no_registration: 'Remind them to register',
  speaker_not_confirmed: 'Confirm they’re coming',
  speaker_room: 'Say whether they need a room',
  speaker_details: 'Ask for the missing details',
  speaker_slides: 'Ask for the slides',
  speaker_glossary: 'Ask for the glossary',
  speaker_prep: 'Hold the prep meeting',
  speaker_no_form: 'Ask for their details',
  parking_code_not_sent: 'Send the parking code',
  eventbrite_core: 'Check whether a refund is needed',
  eventbrite_on_list: 'Check for a double registration',
  eventbrite_other_email: 'Confirm it’s the same person',
  flying_no_hotel: 'Confirm they don’t need a room',
  local_elsewhere: 'Confirm no travel or hotel is needed',
  dietary_unclear: 'Confirm what the answer means',
  accommodations_no_details: 'Ask what they need',
  accommodation_request: 'Arrange with the venue, or reply',
  extra_nights: 'Reply and set the hotel nights',
  hotel_outside_block: 'Confirm rate and billing with the hotel',
  hotel_outside_event: 'Confirm the hotel dates',
  hotel_before_session: 'Confirm the hotel dates',
  hotel_list_no_response: 'Confirm travel, diet and banking',
  hotel_list_unmatched: 'Add them, or check the name',
  hotel_name_mismatch: 'Make sure it matches their ID',
  banking_request: 'Send the direct-deposit request',
  eligibility: 'Decide on the exception',
  travel_exception_pending: 'Waiting on a decision; nothing to do yet',
  pheedloop_not_sent: 'Add them in PheedLoop',
};

const DETAIL_HEAD: Record<string, string> = {
  fellow_no_registration: 'Fellow cohort',
  speaker_room: 'Rooming list',
  speaker_details: 'Still needed',
  speaker_slides: 'Still needed',
  speaker_glossary: 'Still needed',
  speaker_prep: 'Still needed',
  speaker_no_form: 'Still needed',
  parking_code_not_sent: 'Days',
  eventbrite_core: 'Ticket',
  dietary_unclear: 'Their answer',
  accommodation_request: 'Asked for',
  hotel_name_mismatch: 'On the hotel list',
  hotel_outside_block: 'Nights',
  banking_request: 'Banking on file',
  travel_exception_pending: 'Exception',
};

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const dates = (s: string) => s.replace(/\d{4}-\d{2}-\d{2}/g, (d) => monthDay(d)).replace(/\s*,\s*/g, ', ');

/** The particular fact of one item (the group says the rule), and its due date when it has one. */
function particular(i: FollowupItem): { detail: string; due: string | null; overdue: boolean } {
  const m = i.message;
  const dueM = m.match(/ — due (\d{4}-\d{2}-\d{2})( \(overdue\))?\.?$/);
  const base = { due: dueM ? dueM[1] : null, overdue: Boolean(dueM?.[2]) };
  const pick = (re: RegExp, f: (x: RegExpMatchArray) => string) => {
    const x = m.match(re);
    return x ? f(x) : null;
  };
  let detail: string | null = null;
  switch (i.rule) {
    case 'fellow_no_registration':
      detail = pick(/\(([^)]+)\) has not registered( and is not on the list)?/, (x) =>
        x[2] ? `${x[1]}, not on the list` : x[1],
      );
      break;
    case 'speaker_details':
    case 'speaker_slides':
    case 'speaker_glossary':
    case 'speaker_prep':
    case 'speaker_no_form':
      detail = pick(/: (.+?) — due/, (x) => cap(x[1]));
      break;
    case 'speaker_room':
      detail = /^On the rooming list/.test(m) ? 'On the list, not counted' : 'Not on the list';
      break;
    case 'speaker_not_confirmed':
      detail = '';
      break;
    case 'parking_code_not_sent':
      detail = pick(/no code yet for (.+)\.$/, (x) => dates(x[1])) ?? 'Codes assigned, not sent';
      break;
    case 'travel_exception_pending':
      detail = pick(/: (.+) is waiting/, (x) => cap(x[1]));
      break;
    case 'eventbrite_core':
      detail = 'Eventbrite ticket';
      break;
    case 'eventbrite_on_list':
      detail = 'Same person under another email';
      break;
    case 'eventbrite_other_email':
      detail = pick(/as (\S+);/, (x) => `Ticket as ${x[1]}`);
      break;
    case 'flying_no_hotel':
      detail = 'Flying, no hotel';
      break;
    case 'local_elsewhere':
      detail = pick(/, but (.+): confirm/, (x) => cap(x[1]));
      break;
    case 'dietary_unclear':
      detail = pick(/"(.*)"/, (x) => `“${x[1]}”`);
      break;
    case 'accommodations_no_details':
      detail = 'Answered “yes”, no details';
      break;
    case 'accommodation_request':
      detail = pick(/Asked for: "(.*)"\./, (x) => `“${x[1]}”`);
      break;
    case 'extra_nights':
      detail = pick(/\((.+)\): reply/, (x) => cap(dates(x[1])));
      break;
    case 'hotel_outside_block':
      detail = pick(/night\(s\) (.+) are outside/, (x) => dates(x[1]));
      break;
    case 'hotel_outside_event':
    case 'hotel_before_session':
      detail = pick(/^(.+): confirm/, (x) => dates(x[1]));
      break;
    case 'hotel_list_no_response':
      detail = 'No form response';
      break;
    case 'hotel_name_mismatch':
      detail = pick(/Listed as "(.*)"/, (x) => `“${x[1]}”`);
      break;
    case 'hotel_list_unmatched':
      detail = 'Not on the event list';
      break;
    case 'banking_request':
      detail = pick(/on file: (\w+)\./, (x) => cap(x[1]));
      break;
    default:
      detail = null;
  }
  return { ...base, detail: detail ?? m };
}

/** The person cell's name: hotel-list rows without an invitee carry the name in the message. */
const nameOf = (i: FollowupItem) =>
  i.person?.name ?? (i.rule === 'hotel_list_unmatched' ? (i.message.match(/^"(.+?)"/)?.[1] ?? null) : null);

interface Group {
  rule: string;
  title: string;
  items: FollowupItem[];
}

const SHOW = 5;
const anchor = (rule: string) => `fu-${rule}`;
const jump = (rule: string) => document.getElementById(anchor(rule))?.scrollIntoView({ behavior: 'smooth' });
const h2 = 'font-serif font-semibold text-heading text-ink';
const noteCls = 'font-sans font-normal text-[15px] text-ink-3 tabular';
const phoneTd = 'max-md:block max-md:col-start-2 max-md:py-0';

/** Everything to check before the event, grouped by what needs doing; ticked off here. */
export default function FollowupsPanel({ detail, mutable }: PanelProps) {
  const e = detail.event;
  const queryClient = useQueryClient();
  const [filter, setFilterRaw] = useState<Filter>('open');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [kept, setKept] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const list = useQuery<Followups>(
    ['event-followups', e.id],
    async () => (await api.get(`/events/${e.id}/followups`)).data.data,
  );
  const invalidate = () => {
    queryClient.invalidateQueries('event-followups');
    queryClient.invalidateQueries('event-dashboard');
  };
  const save = useMutation(
    async (body: { keys: string[]; state: FollowupItem['state']; note?: string | null }) => {
      for (const key of body.keys) {
        await api.post(`/events/${e.id}/followups/state`, {
          key,
          state: body.state,
          ...(body.note !== undefined ? { note: body.note } : {}),
        });
      }
    },
    {
      onSuccess: (_r, body) => {
        if (body.state !== 'open') setKept((k) => new Set([...k, ...body.keys]));
        setSelected((s) => new Set([...s].filter((k) => !body.keys.includes(k))));
        invalidate();
      },
    },
  );
  const room = useMutation(
    async ({ inviteeId, hotel }: { inviteeId: string; hotel: boolean }) =>
      api.patch(`/events/invitees/${inviteeId}`, { needs: { hotel } }),
    {
      onSuccess: () => {
        ['event-followups', 'event-dashboard', 'event-hotel'].forEach((k) => queryClient.invalidateQueries(k));
      },
    },
  );
  const notSame = useMutation(
    async (inviteeId: string) => api.post(`/events/${e.id}/eventbrite/not-same`, { inviteeId }),
    {
      onSuccess: () => {
        ['event-followups', 'event-dashboard', 'event-invitees'].forEach((k) => queryClient.invalidateQueries(k));
      },
    },
  );
  const setFilter = (f: Filter) => {
    setFilterRaw(f);
    setKept(new Set());
    setSelected(new Set());
  };

  const { groups, info } = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const shown = (list.data?.items ?? [])
      .filter((i) =>
        filter === 'all' ? true : filter === 'open' ? i.state === 'open' || kept.has(i.key) : i.state !== 'open',
      )
      .filter(
        (i) => !needle || `${nameOf(i) ?? ''} ${i.person?.email ?? ''} ${i.message}`.toLowerCase().includes(needle),
      )
      .sort((a, b) => (nameOf(a) ?? '').localeCompare(nameOf(b) ?? ''));
    const titles = new Map((list.data?.byRule ?? []).map((r) => [r.rule, r.title]));
    const by = (items: FollowupItem[]) => {
      const m = new Map<string, Group>();
      for (const i of items) {
        const g = m.get(i.rule) ?? { rule: i.rule, title: titles.get(i.rule) ?? i.title, items: [] };
        g.items.push(i);
        m.set(i.rule, g);
      }
      return [...m.values()].sort((a, b) => b.items.length - a.items.length || a.title.localeCompare(b.title));
    };
    return {
      groups: by(shown.filter((i) => i.severity !== 'info')),
      info: by(shown.filter((i) => i.severity === 'info')),
    };
  }, [list.data, filter, q, kept]);

  if (list.isLoading) return <SkeletonRows rows={6} />;
  if (list.error || !list.data)
    return <ErrorNotice>{errorMessage(list.error, 'Could not load the follow-ups.')}</ErrorNotice>;
  const d = list.data;

  const firstDay = e.days[0]?.date;
  const ended = new Date(e.endsAt) < new Date();
  const refunds = d.items.filter(
    (i) => i.state === 'open' && (i.rule === 'eventbrite_core' || i.rule === 'eventbrite_on_list'),
  ).length;
  const leadText = !d.open
    ? 'Nothing to follow up.'
    : `${fmt(d.open)} ${d.open === 1 ? 'thing' : 'things'} to check${
        firstDay && !ended ? ` before ${monthDay(firstDay)}` : ''
      }. ${
        refunds
          ? `${refunds === 1 ? 'One' : fmt(refunds)} may need a refund.`
          : d.open > 1
            ? 'Most are confirmations.'
            : ''
      }`;
  const sub = !d.open
    ? 'New items appear here when someone’s answers change.'
    : 'Found from registration answers, the hotel list and Eventbrite. A done item stays done unless the person’s answers change.';

  // Viewers: counts by kind and the rule of each, no rows.
  if (!d.namesVisible) {
    const kinds = d.byRule.filter((r) => r.open);
    return (
      <div>
        <Lead lead={leadText} sub={sub} />
        <div className="mt-6">
          <InfoNotice>Counts only: the people and their answers are shown to admins.</InfoNotice>
        </div>
        {kinds.map((r) => (
          <section key={r.rule} className="mt-10 pt-6 border-t border-line-strong max-md:pt-4">
            <h2 className={h2}>
              {r.title} <span className={noteCls}>· {fmt(r.open)} open</span>
            </h2>
            {WHY[r.rule] ? (
              <p className="mt-1 text-[15px] leading-[22px] text-ink-2 max-w-[72ch]">{WHY[r.rule]}</p>
            ) : null}
            {r.done || r.dismissed ? (
              <p className="mt-1 text-small text-ink-3">
                {[r.done ? `${fmt(r.done)} done` : '', r.dismissed ? `${fmt(r.dismissed)} dismissed` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            ) : null}
          </section>
        ))}
      </div>
    );
  }

  const collapse = !q.trim() && groups.length > 6;
  const big = collapse ? groups.filter((g) => g.items.length >= 3 || opened.has(g.rule)) : groups;
  const small = collapse ? groups.filter((g) => g.items.length < 3) : [];
  const smallTotal = small.reduce((n, g) => n + g.items.length, 0);
  const openSmall = (rule: string) => {
    setOpened((o) => new Set([...o, rule]));
    setTimeout(() => jump(rule), 50);
  };
  const toggle = (key: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const busy = save.isLoading || room.isLoading || notSame.isLoading;

  const row = (i: FollowupItem, hasDue: boolean) => {
    const p = particular(i);
    const name = nameOf(i);
    const isOpen = i.state === 'open';
    const sel = selected.has(i.key);
    const who = name ?? 'Not on the list';
    return (
      <tr
        key={i.key}
        className={`border-b border-line max-md:grid max-md:grid-cols-[32px_1fr] max-md:gap-x-3 max-md:gap-y-1 max-md:py-3 ${
          sel ? 'bg-primary-tint' : !isOpen ? 'bg-selected' : ''
        }`}
      >
        <td className={`${td} align-middle max-md:block max-md:py-0 max-md:row-span-1`}>
          {mutable && isOpen ? (
            <input
              type="checkbox"
              className="w-[18px] h-[18px] accent-accent m-0"
              checked={sel}
              onChange={() => toggle(i.key)}
              aria-label={`Select ${who}`}
            />
          ) : null}
        </td>
        <td className={`${td} align-middle max-md:block max-md:py-0`} data-label="Person">
          <span className="font-semibold">{who}</span>
          <span className={`${em} break-words`}>
            {i.person?.email ?? (i.rule === 'pheedloop_not_sent' ? 'Everyone registered' : 'No email on file')}
          </span>
        </td>
        <td className={`${td} align-middle text-ink-2 ${phoneTd}`} data-label={DETAIL_HEAD[i.rule] ?? 'What'}>
          {p.detail || <span className="text-ink-3">–</span>}
          {i.reopened ? <span className={em}>Reopened: the answers changed since it was closed.</span> : null}
          {i.note ? <span className={em}>Note: {i.note}</span> : null}
          {!isOpen && i.doneAt ? (
            <span className={em}>
              {i.state === 'done' ? 'Done' : 'Dismissed'} by {i.doneByEmail ?? 'an operator'} on{' '}
              {new Date(i.doneAt).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
            </span>
          ) : null}
        </td>
        {hasDue ? (
          <td className={`${td} ${num} align-middle text-ink-2 ${phoneTd} max-md:text-left`} data-label="Due">
            {p.due ? (
              <>
                <span className="md:hidden">Due </span>
                <span className={p.overdue ? 'font-semibold text-ink' : ''}>{monthDay(p.due)}</span>
                {p.overdue ? <span className={em}>past its date</span> : null}
              </>
            ) : (
              <span className="text-ink-3 max-md:hidden">–</span>
            )}
          </td>
        ) : null}
        <td className={`${td} text-right align-middle ${phoneTd} max-md:text-left max-md:pt-2`}>
          <div className="inline-flex flex-wrap items-center justify-end gap-2 max-md:justify-start">
            {mutable && isOpen && i.rule === 'speaker_room' && i.inviteeId ? (
              <div
                role="group"
                aria-label={`Does ${who} need a room?`}
                className="inline-flex rounded-sm border border-line-strong overflow-hidden"
              >
                {[
                  [true, 'Needs a room'],
                  [false, 'No room'],
                ].map(([hotel, text], n) => (
                  <button
                    key={String(text)}
                    type="button"
                    aria-pressed={false}
                    disabled={busy}
                    onClick={() => room.mutate({ inviteeId: i.inviteeId!, hotel: hotel as boolean })}
                    className={`min-h-[36px] px-3 text-[15px] leading-5 text-ink hover:bg-selected ${
                      n ? 'border-l border-line-strong' : ''
                    }`}
                  >
                    {text}
                  </button>
                ))}
              </div>
            ) : null}
            {mutable && isOpen && i.rule === 'eventbrite_other_email' && i.inviteeId ? (
              <Button size="sm" variant="quiet" disabled={busy} onClick={() => notSame.mutate(i.inviteeId!)}>
                Not the same person
              </Button>
            ) : null}
            {isOpen ? (
              mutable ? (
                <Button
                  size="sm"
                  disabled={busy}
                  aria-label={`Mark done: ${who}`}
                  onClick={() => save.mutate({ keys: [i.key], state: 'done' })}
                >
                  Done
                </Button>
              ) : null
            ) : (
              <>
                <span className="inline-flex items-center gap-[6px] text-[15px] leading-5 text-ink-2">
                  {i.state === 'done' ? <StatusMark mark="done" /> : <StatusMark mark="off" />}
                  {i.state === 'done' ? 'Done' : 'Dismissed'}
                </span>
                {mutable ? (
                  <Button
                    size="sm"
                    variant="quiet"
                    disabled={busy}
                    aria-label={`Undo: ${who}`}
                    onClick={() => {
                      setKept((k) => {
                        const n = new Set(k);
                        n.delete(i.key);
                        return n;
                      });
                      save.mutate({ keys: [i.key], state: 'open' });
                    }}
                  >
                    Undo
                  </Button>
                ) : null}
              </>
            )}
            {mutable ? (
              <MoreMenu
                label={`More for ${who}`}
                items={[
                  ...(isOpen
                    ? [
                        {
                          label: 'Dismiss',
                          disabled: busy,
                          onClick: () => save.mutate({ keys: [i.key], state: 'dismissed' }),
                        },
                      ]
                    : []),
                  {
                    label: i.note ? 'Edit the note' : 'Add a note',
                    disabled: busy,
                    onClick: async () => {
                      const v = await promptAction(
                        `A note on ${who}: what was done (never banking details).`,
                        i.note ?? '',
                        'Save the note',
                      );
                      if (v !== null) save.mutate({ keys: [i.key], state: i.state, note: v.trim() || null });
                    },
                  },
                ]}
              />
            ) : null}
          </div>
        </td>
      </tr>
    );
  };

  const section = (g: Group) => {
    const hasDue = g.items.some((i) => particular(i).due);
    const all = expanded.has(g.rule);
    const rows = all ? g.items : g.items.slice(0, SHOW);
    const open = g.items.filter((i) => i.state === 'open' && !kept.has(i.key)).length;
    const head = filter === 'open' ? fmt(open) : fmt(g.items.length);
    return (
      <section
        key={g.rule}
        id={anchor(g.rule)}
        className="mt-10 scroll-mt-6 max-md:mt-8 max-md:pt-4 max-md:border-t max-md:border-line-strong"
      >
        <h2 className={h2}>
          {g.title} <span className={noteCls}>· {head}</span>
        </h2>
        {WHY[g.rule] ? (
          <p className="mt-1 mb-3 text-[15px] leading-[22px] text-ink-2 max-w-[72ch]">{WHY[g.rule]}</p>
        ) : (
          <div className="mb-3" />
        )}
        {g.rule === 'eventbrite_core' && open ? (
          <div className="mb-3">
            <ErrorNotice>
              <strong className="font-semibold">Possible double payment.</strong> Check before the ticket’s refund
              window closes.
            </ErrorNotice>
          </div>
        ) : null}
        <table className="w-full table-fixed tabular max-md:block">
          <colgroup className="max-md:hidden">
            <col className="w-[40px]" />
            <col className="w-[32%]" />
            <col />
            {hasDue ? <col className="w-[88px]" /> : null}
            <col className="w-[250px]" />
          </colgroup>
          <thead className="max-md:hidden">
            <tr className={headRow}>
              <th className={th}>
                <span className="sr-only">Select</span>
              </th>
              <th className={th}>Person</th>
              <th className={th}>{DETAIL_HEAD[g.rule] ?? 'What'}</th>
              {hasDue ? <th className={`${th} ${num}`}>Due</th> : null}
              <th className={th}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="max-md:block">{rows.map((i) => row(i, hasDue))}</tbody>
        </table>
        {g.items.length > SHOW ? (
          <button
            type="button"
            className="mt-2 min-h-[44px] text-[15px] leading-[22px] text-accent underline underline-offset-[3px] hover:text-ink"
            onClick={() =>
              setExpanded((x) => {
                const n = new Set(x);
                if (all) n.delete(g.rule);
                else n.add(g.rule);
                return n;
              })
            }
          >
            {all ? 'Show fewer' : `Show the other ${fmt(g.items.length - SHOW)}`}
          </button>
        ) : null}
      </section>
    );
  };

  const jumpList = [
    ...big.map((g) => ({ rule: g.rule, title: g.title, n: g.items.length })),
    ...(small.length
      ? [{ rule: 'smaller', title: `${small.length} smaller ${small.length === 1 ? 'kind' : 'kinds'}`, n: smallTotal }]
      : []),
  ];
  const infoCount = info.reduce((n, g) => n + g.items.length, 0);
  const segments: [Filter, string][] = [
    ['open', 'Open'],
    ['done', 'Done'],
    ['all', 'All'],
  ];

  return (
    <div>
      <Lead lead={leadText} sub={sub} />
      <div className="mt-10 flex flex-wrap gap-x-14 gap-y-6 max-md:mt-6">
        {jumpList.length ? (
          <aside className="flex-[1_1_240px] max-w-[280px] max-md:hidden">
            <h2 className="text-small font-semibold text-ink-2 mb-2">Jump to</h2>
            <nav aria-label="Kinds of follow-up">
              {jumpList.map((j) => (
                <a
                  key={j.rule}
                  href={`#${anchor(j.rule)}`}
                  onClick={(ev) => {
                    ev.preventDefault();
                    jump(j.rule);
                  }}
                  className="flex justify-between gap-3 py-[6px] border-b border-line text-[15px] leading-[22px] text-ink hover:text-accent"
                >
                  {j.title} <span className="text-ink-3 tabular">{fmt(j.n)}</span>
                </a>
              ))}
            </nav>
          </aside>
        ) : null}

        <div className="flex-[999_1_640px] min-w-0">
          <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-line-strong max-md:flex-col max-md:items-stretch">
            {jumpList.length ? (
              <div className="md:hidden">
                <label htmlFor="fu-jump" className="block text-small font-semibold text-ink-2 mb-1">
                  Jump to a kind
                </label>
                <select
                  id="fu-jump"
                  className={`${selectClass} w-full min-h-[44px]`}
                  value=""
                  onChange={(ev) => ev.target.value && jump(ev.target.value)}
                >
                  <option value="">Every kind ({fmt(groups.reduce((n, g) => n + g.items.length, 0))})</option>
                  {jumpList.map((j) => (
                    <option key={j.rule} value={j.rule}>
                      {j.title} ({fmt(j.n)})
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <label htmlFor="fu-search" className="sr-only">
              Search follow-ups
            </label>
            <input
              id="fu-search"
              type="search"
              placeholder="Search by name or email"
              value={q}
              onChange={(ev) => setQ(ev.target.value)}
              className="flex-[1_1_260px] max-md:flex-none min-h-[40px] px-3 py-2 rounded-sm border border-line-strong bg-surface text-[15px] leading-[22px] text-ink max-md:min-h-[44px]"
            />
            <div
              role="group"
              aria-label="Show"
              className="inline-flex rounded-sm border border-line-strong overflow-hidden w-fit"
            >
              {segments.map(([f, text], n) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                  className={`min-h-[40px] px-3 text-[15px] leading-5 ${n ? 'border-l border-line-strong' : ''} ${
                    filter === f ? 'bg-ink text-paper font-semibold' : 'text-ink hover:bg-selected'
                  }`}
                >
                  {text}
                </button>
              ))}
            </div>
            {selected.size ? (
              <div className="flex items-center gap-3 ml-auto max-md:ml-0 max-md:bg-primary-tint max-md:rounded-sm max-md:p-3">
                <span className="text-[15px] leading-5 text-ink max-md:flex-1">{fmt(selected.size)} selected</span>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => save.mutate({ keys: [...selected], state: 'done' })}
                >
                  {save.isLoading ? 'Saving…' : 'Mark done'}
                </Button>
                <Button size="sm" variant="quiet" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
            ) : null}
          </div>

          {save.error || room.error ? (
            <div className="mt-4">
              <ErrorNotice>{errorMessage(save.error || room.error, 'Could not save.')}</ErrorNotice>
            </div>
          ) : null}

          {!groups.length && !info.length ? (
            q.trim() ? (
              <EmptyState title={`Nothing matches “${q.trim()}”.`}>
                <button
                  type="button"
                  className="text-accent underline underline-offset-[3px] hover:text-ink"
                  onClick={() => setQ('')}
                >
                  Clear the search
                </button>{' '}
                to see every follow-up.
              </EmptyState>
            ) : filter === 'open' ? (
              <EmptyState title="Nothing to follow up.">
                New items appear here when someone’s answers change.
              </EmptyState>
            ) : (
              <EmptyState title={filter === 'done' ? 'Nothing is done or dismissed yet.' : 'No follow-ups.'}>
                Items you mark done or dismiss are listed here.
              </EmptyState>
            )
          ) : null}

          {big.map(section)}

          {small.length || infoCount ? (
            <section
              id={anchor('smaller')}
              className="mt-12 scroll-mt-6 max-md:pt-4 max-md:border-t max-md:border-line-strong"
            >
              {small.length ? (
                <>
                  <h2 className={`${h2} mb-3`}>
                    Smaller kinds{' '}
                    <span className={noteCls}>
                      · {fmt(smallTotal)} in {fmt(small.length)} kinds
                    </span>
                  </h2>
                  <table className="w-full tabular max-md:table">
                    <thead>
                      <tr className={headRow}>
                        <th className={th}>Kind</th>
                        <th className={`${th} max-md:hidden`}>What to do</th>
                        <th className={`${th} ${num} w-[80px]`}>{filter === 'open' ? 'Open' : 'Items'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {small.map((g) => (
                        <tr key={g.rule} className="border-b border-line">
                          <td className={td}>
                            <button
                              type="button"
                              className="text-left text-accent underline underline-offset-[3px] hover:text-ink"
                              onClick={() => openSmall(g.rule)}
                            >
                              {g.title}
                            </button>
                            <span className={`${em} md:hidden`}>{TODO[g.rule] ?? ''}</span>
                          </td>
                          <td className={`${td} text-ink-2 max-md:hidden`}>
                            {TODO[g.rule] ?? <span className="text-ink-3">–</span>}
                          </td>
                          <td className={`${td} ${num}`}>{fmt(g.items.length)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : null}
              {info.map((g) => (
                <p key={g.rule} className="mt-4 text-[15px] leading-[22px] text-ink-2">
                  For information, not counted:{' '}
                  <button
                    type="button"
                    className="text-accent underline underline-offset-[3px] hover:text-ink"
                    onClick={() => openSmall(`info:${g.rule}`)}
                  >
                    {g.rule === 'submitted_twice'
                      ? `${fmt(g.items.length)} ${g.items.length === 1 ? 'person' : 'people'} submitted the form more than once`
                      : `${g.title} (${fmt(g.items.length)})`}
                  </button>
                  {g.rule === 'submitted_twice' ? '; their latest answers are kept.' : '.'}
                </p>
              ))}
            </section>
          ) : null}
          {info.filter((g) => opened.has(`info:${g.rule}`)).map((g) => section({ ...g, rule: `info:${g.rule}` }))}
        </div>
      </div>
    </div>
  );
}
